import Config from "../Config";
import { useState, useEffect, useRef } from "react";

import direction from "../constants/SortOrder";

export const useLocalPaginatedRequest = (
    requestFn = () => Promise.resolve(),
    params = {},
    deps = [],
    searchKey = "Q",
    dataKey = "data",
    totalKey = "count",
    dataId = null 
) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(Config.LIMIT);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState("");
    const [total, setTotal] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const requestAbortController = useRef(null);
    // Bumped on every request(), and compared against when a response
    // lands — see request() below for why this exists on top of
    // AbortController.
    const requestIdRef = useRef(0);

    useEffect(() => {
        requestAbortController.current = new AbortController();

        request();

        return () => {
            if (requestAbortController.current) {
                requestAbortController.current.abort();
            }
        };
        // eslint-disable-next-line
    }, [page,pageSize, searchText, ...deps]);

    const request = () => {
        setIsFetching(true);
        // Switching filters fast enough (e.g. clicking a status tab twice
        // in a row) can start a second request before the first one's
        // response has landed. AbortController's signal is passed through
        // to cancel that first one at the transport level, but nothing here
        // actually depended on the abort succeeding — if it lands anyway
        // (a response already in flight when abort() fires, a consumer of
        // this hook that doesn't wire the signal all the way through), the
        // old .then() would still run and overwrite data/total with a
        // stale filter's rows. Stamping and checking a request id fixes
        // that unconditionally: whichever request started last is the only
        // one whose response is ever applied, regardless of arrival order.
        const requestId = ++requestIdRef.current;

        requestFn(
            {
                limit: pageSize,
                page: page,
                // direction: direction.DESC,
                // column: "CreatedAt",
                ...(searchText && { [searchKey]: searchText }),
                ...(Object.keys(params).length && params)
            },
            requestAbortController.current.signal,
            dataId
        )
            .then((res) => {
                if (requestId !== requestIdRef.current) return;
                setData(res[dataKey]);
                setTotal(res[totalKey]);
            })
            .catch(() => {
                // Aborted, or a genuine failure — either way there's
                // nothing to apply. A real failure's message is already
                // surfaced by the shared fetch error handler's toast
                // (utils/errorHandler.js); this only exists so the
                // rejection doesn't go unhandled.
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setIsFetching(false);
            });
    };

    const handleSearchChange = (e) => {
        let searchText = e.target.value;
        setSearchText(searchText);
        setPage(1);
    };

    const handlePageClick = (pageNo) => {
        setPage(pageNo);
    };

    const onChangePageSize = (event) => {
        setPageSize(Number(event.target.value));
        setPage(1); 
    };

    const setPageToOne = () => {
        setPage(1);
    }

    return {
        page,
        searchText,
        data,
        total,
        isFetching,
        request,
        pageSize,
        onChangePageSize,
        handleSearchChange,
        handlePageClick,
        setPageToOne
    };
};
